import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Image,
  FileText,
  Video,
  Calendar,
  Trophy,
  Users,
  Medal,
  BarChart3,
  LogOut,
  Menu,
  X,
  HandHelping,
  Settings,
  Shield,
  ClipboardList,
  Zap,
  Users2,
  ChevronDown,
  Database,
  GitBranch
} from 'lucide-react';
import { useEffect, useState } from 'react';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  ));
  const { user, logout, updateActivity } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard, roles: ['super_admin'] },
    {
      name: 'Event',
      icon: Zap,
      roles: ['super_admin'],
      submenu: [
        { name: 'Kelola Event', path: '/admin/events', icon: Zap, roles: ['super_admin'] },
        { name: 'Pendaftaran Tim', path: '/admin/events/teams', icon: Users2, roles: ['super_admin'] },
        { name: 'Hasil Drawing', path: '/admin/events/brackets', icon: GitBranch, roles: ['super_admin'] },
        { name: 'Drawing', path: '/admin/events/matches', icon: Calendar, roles: ['super_admin'] },
        { name: 'Pertandingan', path: '/admin/matches', icon: Calendar, roles: ['super_admin'] },
      ]
    },
    {
      name: 'Peserta',
      icon: Users,
      roles: ['super_admin', 'club_admin'],
      submenu: [
        { name: 'Peserta Klub', path: '/admin/participants', icon: Users, roles: ['super_admin', 'club_admin'] },
        { name: 'Peserta Sekolah', path: '/admin/school-participants', icon: Users, roles: ['super_admin'] },
        { name: 'Pemain', path: '/admin/players', icon: Users2, roles: ['super_admin'] },
      ]
    },
    { name: 'Klasemen', path: '/admin/standings', icon: Trophy, roles: ['super_admin'] },
    { name: 'Top Skor', path: '/admin/top-scorers', icon: Medal, roles: ['super_admin'] },
    { name: 'Banner', path: '/admin/banners', icon: Image, roles: ['super_admin'] },
    { name: 'Iklan', path: '/admin/advertisements', icon: BarChart3, roles: ['super_admin'] },
    { name: 'Berita', path: '/admin/news', icon: FileText, roles: ['super_admin'] },
    { name: 'Foto', path: '/admin/photos', icon: Image, roles: ['super_admin'] },
    { name: 'Video', path: '/admin/videos', icon: Video, roles: ['super_admin'] },
    { name: 'Hubungi Kami', path: '/admin/hubungi-kami', icon: HandHelping, roles: ['super_admin'] },
    { name: 'Kelola Admin Klub', path: '/admin/club-admins', icon: Shield, roles: ['super_admin'] },
    { name: 'Master Data', path: '/admin/master-data', icon: Database, roles: ['super_admin'] },
    { name: 'Audit Log', path: '/admin/audit-logs', icon: ClipboardList, roles: ['super_admin'] },
    { name: 'Pengaturan', path: '/admin/settings', icon: Settings, roles: ['super_admin', 'club_admin'] },
  ].filter((item) => item.roles.includes(user?.role || 'super_admin'));

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const activeParent = menuItems.find(
      (item) => item.submenu?.some((subitem) => subitem.path === location.pathname)
    );

    if (activeParent) {
      setExpandedSubmenu(activeParent.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Track user activity for session timeout
  useEffect(() => {
    const handleActivity = () => {
      updateActivity();
    };

    const events = ['click', 'keypress', 'mousemove', 'touchstart', 'scroll'];
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [updateActivity]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-4 bg-gray-800">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_90560e06-7136-451f-8b9f-e5b1a310f4fa/artifacts/3z4o6h4i_2133.png"
                alt="IJSL"
                className="h-10 w-auto brightness-0 invert"
              />
              <span className="text-white font-bold text-lg">Admin IJSL</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.path ? location.pathname === item.path : false;
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isExpanded = expandedSubmenu === item.name;
              
              if (hasSubmenu) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setExpandedSubmenu(isExpanded ? null : item.name)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isExpanded
                          ? 'bg-gray-800 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="h-5 w-5" />
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {isExpanded && (
                      <div className="pl-4 space-y-1 mt-1">
                        {item.submenu.map((subitem) => {
                          const SubIcon = subitem.icon;
                          const isSubActive = location.pathname === subitem.path;
                          return (
                            <Link
                              key={subitem.path}
                              to={subitem.path}
                              onClick={() => {
                                if (!isDesktop) {
                                  setSidebarOpen(false);
                                }
                              }}
                              className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                                isSubActive
                                  ? 'bg-green-600 text-white'
                                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                              }`}
                            >
                              <SubIcon className="h-4 w-4" />
                              <span className="text-sm">{subitem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-green-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="px-4 py-4 bg-gray-800 border-t border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-green-600 rounded-full h-10 w-10 flex items-center justify-center">
                  <span className="text-white font-bold">{user?.username?.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{user?.username}</p>
                  <p className="text-gray-400 text-xs">{user?.role === 'club_admin' ? (user?.participant_name || user?.email) : user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-white transition-colors"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {menuItems.find((item) => item.path === location.pathname)?.name || 'Admin Panel'}
            </h1>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                target="_blank"
                className="text-green-600 hover:text-green-700 font-medium text-sm"
              >
                Lihat Website →
              </Link>
              {/* Emergent logo removed */}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;